import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { supabase } from '@/lib/supabase';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ArrowLeft, Play, AlertCircle, Check, Trophy, Pencil } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const CONDITION_TYPES = [
 { value: 'add_court', label: 'Ajouter des terrains' },
 { value: 'activate_court', label: 'Valider des terrains' },
 { value: 'win_duels', label: 'Gagner des duels' },
 { value: 'capture_courts', label: 'Conquérir des terrains' },
 { value: 'defend_courts', label: 'Défendre des terrains' },
 { value: 'community_growth', label: 'Développer la communauté' }
];


export default function AdminChallengeDetail({ challengeIdProp }) {
 const { challengeId: routeChallengeId } = useParams();
 const navigate = useNavigate();
 const challengeId = challengeIdProp || routeChallengeId;
 const [user, setUser] = useState(null);
 const [dialogOpen, setDialogOpen] = useState(false);
 const [dialogTitle, setDialogTitle] = useState('');
 const [dialogMessage, setDialogMessage] = useState('');
 const [drawConfirmOpen, setDrawConfirmOpen] = useState(false);
 const [isEditing, setIsEditing] = useState(false);
 const [editData, setEditData] = useState(null);
 const queryClient = useQueryClient();


 React.useEffect(() => {
   supabase.auth.getUser().then(async ({ data: { user: authUser } }) => {
       if (!authUser) { navigate('/map'); return; }
       const { data: profile } = await supabase.from('users').select('*').eq('id', authUser.id).single();
       if (!profile || profile.role !== 'admin') {
         navigate('/map');
       } else {
         setUser({ ...authUser, ...profile });
       }
     }).catch(() => navigate('/map'));
 }, [navigate]);


 const { data: challenge, isLoading } = useQuery({
   queryKey: ['challengeDetail', challengeId],
   queryFn: async () => {
     if (!challengeId) return null;
     try {
       // Direct fetch by ID is more reliable than listing all
       const result = await base44.entities.Challenge.filter({ id: challengeId });
       return result?.[0] || null;
     } catch {
       return null;
     }
   },
   enabled: !!challengeId && !!user?.id
 });


 const { data: participants = [] } = useQuery({
   queryKey: ['challengeParticipants', challengeId],
   queryFn: async () => {
     // Fetch both RaffleEntry (tickets) and UserChallengeProgress (progress)
     const [entries, progressRecords] = await Promise.all([
       base44.entities.RaffleEntry.filter({ challenge_id: challengeId }),
       base44.entities.UserChallengeProgress.filter({ challenge_id: challengeId })
     ]);

     // Build a combined map: user_id -> { tickets, progress_value, is_completed }
     const userMap = {};

     // Add progress data first
     progressRecords.forEach(p => {
       userMap[p.user_id] = {
         tickets: 0,
         progress_value: p.progress_value || 0,
         is_completed: p.is_completed || false
       };
     });

     // Add ticket counts
     entries.forEach(entry => {
       if (!userMap[entry.user_id]) {
         userMap[entry.user_id] = { tickets: 0, progress_value: 0, is_completed: false };
       }
       userMap[entry.user_id].tickets++;
     });

     const participantList = Object.entries(userMap).map(([user_id, data]) => ({
       user_id,
       tickets: data.tickets,
       progress_value: data.progress_value,
       is_completed: data.is_completed
     }));

     // Fetch usernames for all participants
     const userInfos = await Promise.all(
       participantList.map(p =>
         supabase.from('users').select('username, full_name').eq('id', p.user_id).single()
           .then(({ data }) => ({ user_id: p.user_id, username: data?.username || data?.full_name || null }))
           .catch(() => ({ user_id: p.user_id, username: null }))
       )
     );
     const usernameMap = {};
     userInfos.forEach(u => { usernameMap[u.user_id] = u.username; });

     return participantList.map(p => ({
       ...p,
       username: usernameMap[p.user_id] || p.user_id.slice(0, 8) + '...'
     }));
   },
   enabled: !!challengeId
 });


 const { data: draw } = useQuery({
   queryKey: ['challengeDraw', challengeId],
   queryFn: async () => {
     if (!challengeId) return null;

     // Fetch draw by challenge_id only (not by raffle_month)
     // This is important for challenges that span multiple months
     const draws = await base44.entities.ChallengeDraw.filter({
       challenge_id: challengeId
     });
     return draws?.[0] || null;
   },
   enabled: !!challenge?.id
 });


 const { data: winner } = useQuery({
   queryKey: ['winnerInfo', draw?.winner_user_id],
   queryFn: async () => {
     if (!draw?.winner_user_id) return null;
     const { data: userInfo } = await supabase.from('users').select('*').eq('id', draw.winner_user_id).single();
     return userInfo || null;
   },
   enabled: !!draw?.winner_user_id
 });


 const executeDrawMutation = useMutation({
   mutationFn: async () => {
     if (!challenge?.id) throw new Error('Challenge not found');
     const raffle_month = `${new Date(challenge.end_at).getFullYear()}-${String(new Date(challenge.end_at).getMonth() + 1).padStart(2, '0')}`;
    
     console.warn('[Migration] executeChallengeDraw not fully migrated yet');
     return null;
   },
   onSuccess: (data) => {
     // Log admin action (best-effort)
     try {
       base44.entities.AdminLog?.create?.({
         admin_user_id: user.id,
         action_type: 'challenge_draw',
         target_entity: 'Challenge',
         target_id: challengeId,
         details: {
           finalists_count: data?.finalists_count,
           total_participants: data?.total_participants,
           winner_user_id: data?.winner_user_id
         },
         timestamp: new Date().toISOString()
       })?.catch?.(err => console.error('Failed to log draw action:', err));
     } catch (e) {
       console.error('Failed to log draw action:', e);
     }

     setDialogTitle('Tirage réussi');
     setDialogMessage(`${data?.finalists_count || '?'} finalistes parmi ${data?.total_participants || '?'} participants.`);
     setDialogOpen(true);
     setDrawConfirmOpen(false);
     queryClient.invalidateQueries({ queryKey: ['challengeDetail', challenge?.id] });
     queryClient.invalidateQueries({ queryKey: ['challengeDraw', challenge?.id] });
   },
   onError: (error) => {
     setDialogTitle('Erreur');
     setDialogMessage(error.response?.data?.error || error.message);
     setDialogOpen(true);
   }
 });


 const updateChallengeMutation = useMutation({
   mutationFn: async (data) => {
     return base44.entities.Challenge.update(challengeId, {
       title: data.title,
       description: data.description,
       condition_type: data.condition_type,
       condition_value: parseInt(data.condition_value),
       reward_value: parseInt(data.reward_value),
       reward_description: data.reward_description,
       min_level: parseInt(data.min_level) || 1,
       start_at: new Date(data.start_at).toISOString(),
       end_at: new Date(data.end_at).toISOString()
     });
   },
   onSuccess: () => {
     setIsEditing(false);
     setEditData(null);
     queryClient.invalidateQueries({ queryKey: ['challengeDetail', challengeId] });
     queryClient.invalidateQueries({ queryKey: ['adminChallenges'] });
     setDialogTitle('Succès');
     setDialogMessage('Challenge modifié avec succès');
     setDialogOpen(true);
   },
   onError: (error) => {
     setDialogTitle('Erreur');
     setDialogMessage('Erreur: ' + (error.message || 'Échec de la modification'));
     setDialogOpen(true);
   }
 });

 function startEditing() {
   if (!challenge) return;
   // Convert ISO dates to datetime-local format
   const toLocal = (iso) => {
     const d = new Date(iso);
     const offset = d.getTimezoneOffset();
     const local = new Date(d.getTime() - offset * 60000);
     return local.toISOString().slice(0, 16);
   };
   setEditData({
     title: challenge.title || '',
     description: challenge.description || '',
     condition_type: challenge.condition_type || '',
     condition_value: String(challenge.condition_value || ''),
     reward_value: String(challenge.reward_value || ''),
     reward_description: challenge.reward_description || '',
     min_level: String(challenge.min_level || 1),
     start_at: toLocal(challenge.start_at),
     end_at: toLocal(challenge.end_at)
   });
   setIsEditing(true);
 }

 if (!user || isLoading) {
   return <div className="flex items-center justify-center min-h-screen text-white">Chargement...</div>;
 }


 if (!challenge) {
   return (
     <div className="bg-[#0A0B10] text-white p-6">
       <div className="max-w-4xl mx-auto text-center">
         <p className="text-[#B3B6C2]">Challenge introuvable</p>
         <Link to="/admin/challenges" className="text-[#6E2BFF] hover:text-[#8B5CFF] mt-4 inline-block">
           Retour aux Challenges
         </Link>
       </div>
     </div>
   );
 }


 const canExecuteDraw = challenge.status === 'closed' && !draw;
 const hasBeenDrawn = challenge.status === 'drawn' && draw;
 const totalTickets = participants.reduce((sum, p) => sum + p.tickets, 0);


 return (
   <>
     <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
       <DialogContent className="bg-[#13151D] border-[#6E2BFF] text-white">
         <DialogTitle className="text-white">{dialogTitle}</DialogTitle>
         <DialogDescription className="text-[#B3B6C2]">{dialogMessage}</DialogDescription>
         <div className="flex justify-end gap-2 mt-4">
           <Button onClick={() => setDialogOpen(false)} className="bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white">
             OK
           </Button>
         </div>
       </DialogContent>
     </Dialog>


     <Dialog open={drawConfirmOpen} onOpenChange={setDrawConfirmOpen}>
       <DialogContent className="bg-[#13151D] border-[#6E2BFF] text-white">
         <DialogTitle className="text-white">⚠️ Lancer le tirage</DialogTitle>
         <DialogDescription className="text-[#B3B6C2] space-y-3">
           <p>Cette action est définitive.</p>
           <p>Le tirage ne peut pas être annulé ou relancé.</p>
           <p className="font-bold text-white">Voulez-vous continuer?</p>
         </DialogDescription>
         <div className="flex justify-end gap-2 mt-4">
           <Button
             onClick={() => setDrawConfirmOpen(false)}
             variant="outline"
             className="border-[#6E2BFF] text-[#6E2BFF]"
           >
             Annuler
           </Button>
           <Button
             onClick={() => executeDrawMutation.mutate()}
             disabled={executeDrawMutation.isPending}
             className="bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white"
           >
             {executeDrawMutation.isPending ? 'Tirage...' : 'Confirmer'}
           </Button>
         </div>
       </DialogContent>
     </Dialog>


     <div className="bg-[#0A0B10] text-white p-6">
       <div className="max-w-4xl mx-auto">
         {/* Header */}
         <div className="mb-6 flex items-center gap-4">
           <Button
             variant="ghost"
             size="sm"
             className="text-[#B3B6C2] hover:text-white"
             onClick={() => navigate('/admin/challenges')}
           >
             <ArrowLeft className="w-4 h-4 mr-2" /> Retour
           </Button>
           <h1 className="text-3xl font-black uppercase tracking-tight">{challenge.title}</h1>
           {challenge.status !== 'drawn' && !isEditing && (
             <Button
               onClick={startEditing}
               variant="outline"
               size="sm"
               className="ml-auto border-[#6E2BFF] text-[#6E2BFF] hover:bg-[#6E2BFF]/10"
             >
               <Pencil className="w-4 h-4 mr-2" /> Modifier
             </Button>
           )}
         </div>


         <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
           {/* Main Info */}
           <div className="md:col-span-2 space-y-6">
             {/* Challenge Info - Edit or Read mode */}
             {isEditing && editData ? (
               <Card className="bg-[#13151D] border-l-4 border-amber-500 p-6 text-white space-y-4">
                 <h2 className="text-lg font-black uppercase tracking-tight mb-2">Modifier le challenge</h2>
                 <div>
                   <label className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold block mb-2">Titre</label>
                   <Input
                     value={editData.title}
                     onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                     className="bg-[#1B1E28] border-[#6E2BFF] text-white"
                   />
                 </div>
                 <div>
                   <label className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold block mb-2">Description</label>
                   <Textarea
                     value={editData.description}
                     onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                     className="bg-[#1B1E28] border-[#6E2BFF] text-white"
                   />
                 </div>
                 <div className="grid grid-cols-2 gap-4">
                   <div>
                     <label className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold block mb-2">Type de défi</label>
                     <Select value={editData.condition_type} onValueChange={(v) => setEditData({ ...editData, condition_type: v })}>
                       <SelectTrigger className="bg-[#1B1E28] border-[#6E2BFF] text-white h-10">
                         <SelectValue />
                       </SelectTrigger>
                       <SelectContent className="bg-[#1B1E28] border-[#6E2BFF]">
                         {CONDITION_TYPES.map(t => (
                           <SelectItem key={t.value} value={t.value} className="text-white">{t.label}</SelectItem>
                         ))}
                       </SelectContent>
                     </Select>
                   </div>
                   <div>
                     <label className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold block mb-2">Objectif</label>
                     <Input
                       type="number"
                       value={editData.condition_value}
                       onChange={(e) => setEditData({ ...editData, condition_value: e.target.value })}
                       className="bg-[#1B1E28] border-[#6E2BFF] text-white"
                     />
                   </div>
                 </div>
                 <div className="grid grid-cols-2 gap-4">
                   <div>
                     <label className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold block mb-2">Récompense (tickets)</label>
                     <Input
                       type="number"
                       value={editData.reward_value}
                       onChange={(e) => setEditData({ ...editData, reward_value: e.target.value })}
                       className="bg-[#1B1E28] border-[#6E2BFF] text-white"
                     />
                   </div>
                   <div>
                     <label className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold block mb-2">Description récompense</label>
                     <Input
                       value={editData.reward_description}
                       onChange={(e) => setEditData({ ...editData, reward_description: e.target.value })}
                       className="bg-[#1B1E28] border-[#6E2BFF] text-white"
                     />
                   </div>
                 </div>
                 <div>
                   <label className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold block mb-2">Niveau minimum requis</label>
                   <Select value={editData.min_level} onValueChange={(v) => setEditData({ ...editData, min_level: v })}>
                     <SelectTrigger className="bg-[#1B1E28] border-[#6E2BFF] text-white h-10">
                       <SelectValue />
                     </SelectTrigger>
                     <SelectContent className="bg-[#1B1E28] border-[#6E2BFF]">
                       <SelectItem value="1" className="text-white">Niveau 1 (tous)</SelectItem>
                       <SelectItem value="3" className="text-white">Niveau 3</SelectItem>
                       <SelectItem value="5" className="text-white">Niveau 5</SelectItem>
                       <SelectItem value="10" className="text-white">Niveau 10</SelectItem>
                     </SelectContent>
                   </Select>
                 </div>
                 <div className="grid grid-cols-2 gap-4">
                   <div>
                     <label className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold block mb-2">Début</label>
                     <Input
                       type="datetime-local"
                       value={editData.start_at}
                       onChange={(e) => setEditData({ ...editData, start_at: e.target.value })}
                       className="bg-[#1B1E28] border-[#6E2BFF] text-white"
                     />
                   </div>
                   <div>
                     <label className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold block mb-2">Fin</label>
                     <Input
                       type="datetime-local"
                       value={editData.end_at}
                       onChange={(e) => setEditData({ ...editData, end_at: e.target.value })}
                       className="bg-[#1B1E28] border-[#6E2BFF] text-white"
                     />
                   </div>
                 </div>
                 <div className="flex gap-2 pt-2">
                   <Button
                     onClick={() => updateChallengeMutation.mutate(editData)}
                     disabled={updateChallengeMutation.isPending}
                     className="flex-1 bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white font-bold"
                   >
                     {updateChallengeMutation.isPending ? 'Enregistrement...' : 'Enregistrer'}
                   </Button>
                   <Button
                     onClick={() => { setIsEditing(false); setEditData(null); }}
                     variant="outline"
                     className="flex-1 border-[#6E2BFF] text-[#6E2BFF]"
                   >
                     Annuler
                   </Button>
                 </div>
               </Card>
             ) : (
             <Card className="bg-[#13151D] border-l-4 border-[#6E2BFF] p-6 text-white">
               <h2 className="text-lg font-black uppercase tracking-tight mb-4">Informations</h2>
               <div className="space-y-3">
                 <div>
                   <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold">Description</p>
                   <p className="text-white mt-1">{challenge.description}</p>
                 </div>
                 <div className="grid grid-cols-2 gap-4 pt-3 border-t border-[#1B1E28]">
                   <div>
                     <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold">Statut</p>
                     <p className="text-white font-bold mt-1 text-xs">{challenge?.status?.toUpperCase() || 'UNKNOWN'}</p>
                   </div>
                   <div>
                     <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold">Actif</p>
                     <p className="text-white font-bold mt-1 text-xs">{challenge.is_active ? 'Oui' : 'Non'}</p>
                   </div>
                 </div>
                 <div className="grid grid-cols-2 gap-4 pt-3 border-t border-[#1B1E28]">
                   <div>
                     <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold">Début</p>
                     <p className="text-white text-sm mt-1">{new Date(challenge.start_at).toLocaleString('fr-FR')}</p>
                   </div>
                   <div>
                     <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold">Fin</p>
                     <p className="text-white text-sm mt-1">{new Date(challenge.end_at).toLocaleString('fr-FR')}</p>
                   </div>
                 </div>
                 {challenge.min_level > 1 && (
                   <div className="pt-3 border-t border-[#1B1E28]">
                     <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold">Niveau minimum</p>
                     <p className="text-white font-bold mt-1">{challenge.min_level}</p>
                   </div>
                 )}
               </div>
             </Card>
             )}


             {/* Participation Stats */}
             <Card className="bg-[#13151D] border-l-4 border-[#6E2BFF] p-6 text-white">
               <h2 className="text-lg font-black uppercase tracking-tight mb-4">Participation</h2>
               <div className="grid grid-cols-3 gap-4">
                 <div className="bg-[#1B1E28] p-4 rounded-lg">
                   <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold">Participants</p>
                   <p className="text-3xl font-black text-[#6E2BFF] mt-2">{participants.length}</p>
                 </div>
                 <div className="bg-[#1B1E28] p-4 rounded-lg">
                   <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold">Complétés</p>
                   <p className="text-3xl font-black text-green-500 mt-2">{participants.filter(p => p.is_completed).length}</p>
                 </div>
                 <div className="bg-[#1B1E28] p-4 rounded-lg">
                   <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold">Total Tickets</p>
                   <p className="text-3xl font-black text-white mt-2">{totalTickets}</p>
                 </div>
               </div>

               {/* Participant Details */}
               {participants.length > 0 && (
                 <div className="mt-4 pt-4 border-t border-[#1B1E28]">
                   <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold mb-3">Détail des participants</p>
                   <div className="space-y-2 max-h-48 overflow-y-auto">
                     {participants
                       .sort((a, b) => b.progress_value - a.progress_value)
                       .map((p, idx) => (
                       <div key={p.user_id} className="flex items-center justify-between bg-[#0A0B10] p-2 rounded text-xs">
                         <span className="text-[#B3B6C2] truncate flex-1">#{idx + 1} {p.username}</span>
                         <div className="flex items-center gap-3">
                           <span className="text-white font-bold">{p.progress_value}</span>
                           <span className="text-[#6E2BFF] font-bold">{p.tickets} ticket{p.tickets !== 1 ? 's' : ''}</span>
                           {p.is_completed && <span className="text-green-400 font-bold">OK</span>}
                         </div>
                       </div>
                     ))}
                   </div>
                 </div>
               )}

               <div className="mt-4 pt-4 border-t border-[#1B1E28]">
                 <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold mb-2">Récompense</p>
                 <p className="text-white">{challenge.reward_value} chance{challenge.reward_value !== 1 ? 's' : ''} par complétion</p>
               </div>
             </Card>
           </div>


           {/* Draw Section */}
           <div>
             <Card className="bg-[#13151D] border-l-4 border-amber-600 p-6 h-full text-white">
               <h2 className="text-lg font-black uppercase tracking-tight mb-4">Tirage</h2>
              
               {challenge.status !== 'closed' && !hasBeenDrawn && (
                 <div className="bg-[#1B1E28] border border-[#6E2BFF] p-4 rounded-lg text-center">
                   <AlertCircle className="w-6 h-6 text-[#6E2BFF] mx-auto mb-2" />
                   <p className="text-sm text-[#B3B6C2]">Tirage disponible à la fin du challenge</p>
                 </div>
               )}


               {canExecuteDraw && (
                 <>
                   <div className="bg-[#1B1E28] border border-[#6E2BFF] p-4 rounded-lg mb-4 text-center">
                     <AlertCircle className="w-6 h-6 text-[#6E2BFF] mx-auto mb-2" />
                     <p className="text-xs text-[#B3B6C2] font-semibold">Action définitive</p>
                     <p className="text-xs text-[#B3B6C2] mt-1">Le tirage ne peut être relancé</p>
                   </div>
                   <Button
                     onClick={() => setDrawConfirmOpen(true)}
                     disabled={executeDrawMutation.isPending}
                     className="w-full bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white font-bold"
                   >
                     <Play className="w-4 h-4 mr-2" />
                     {executeDrawMutation.isPending ? 'Tirage...' : 'Lancer le tirage'}
                   </Button>
                 </>
               )}


               {hasBeenDrawn && draw && (
                 <div className="space-y-4">
                   <div className="bg-[#1B1E28] border border-[#6E2BFF] p-3 rounded-lg text-center">
                     <Check className="w-5 h-5 text-[#6E2BFF] mx-auto mb-1" />
                     <p className="text-xs font-bold text-[#6E2BFF]">Tirage exécuté</p>
                   </div>


                   <div className="bg-[#1B1E28] p-3 rounded-lg">
                     <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold mb-1">Gagnant</p>
                     <p className="text-white font-bold">{winner?.full_name || 'Inconnu'}</p>
                   </div>


                   <div className="bg-[#1B1E28] p-3 rounded-lg">
                     <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold mb-2">Finalistes ({draw.finalists_count})</p>
                     <div className="space-y-1 max-h-32 overflow-y-auto">
                       {draw.finalists_user_ids?.slice(0, 10).map((userId, idx) => (
                         <div key={idx} className="text-xs text-[#B3B6C2]">#{idx + 1}</div>
                       ))}
                     </div>
                   </div>


                   <div className="bg-[#1B1E28] p-3 rounded-lg text-xs">
                     <p className="uppercase tracking-widest text-[#B3B6C2] font-semibold">Date</p>
                     <p className="text-white mt-1">{new Date(draw.draw_date).toLocaleString('fr-FR')}</p>
                   </div>


                   <div className="bg-[#1B1E28] p-3 rounded-lg text-xs">
                     <p className="uppercase tracking-widest text-[#B3B6C2] font-semibold">Seed</p>
                     <p className="text-white font-mono text-[10px] mt-1 break-all">{draw.random_seed}</p>
                   </div>
                 </div>
               )}
             </Card>
           </div>
         </div>
       </div>
     </div>
   </>
 );
}